import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface LinkedInWizardStep5Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const SENIORITY_LEVELS = [
  "Entry Level",
  "Mid Level", 
  "Director Level",
  "VP Level",
  "Executive Level",
  "I'm not sure"
];

const COMPANY_SIZES = [
  "Startup (1-50 employees)",
  "SMB (51-500 employees)",
  "Enterprise (500+ employees)",
  "Mix of all sizes",
  "I'm not sure"
];

export function LinkedInWizardStep5({ data, updateData }: LinkedInWizardStep5Props) {

  return (
    <div className="space-y-6">
      {/* Step Number and Header */}
      <div className="relative">
        <div className="absolute -top-2 -left-2">
          <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
            <span className="text-2xl font-bold text-white">5</span>
          </div>
        </div>
        <div className="ml-12">
          <h2 className="text-2xl font-bold mb-2 text-linkedin">Define Your Target Audience</h2>
          <p className="text-linkedin/70 mb-6">
            Specify the seniority level and company size of your ideal LinkedIn connections.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="seniority" className="text-sm font-medium text-linkedin">
              Seniority of your ideal audience *
            </Label>
            <Select 
              value={data.audienceSeniority || ""} 
              onValueChange={(value) => updateData({ audienceSeniority: value })}
            >
              <SelectTrigger className={`border-linkedin/20 ${data.audienceSeniority ? 'border-green-500 bg-green-50 dark:bg-green-950 ring-2 ring-green-200' : ''}`}>
                <SelectValue 
                  placeholder="Select seniority level" 
                  className={`${data.audienceSeniority ? 'text-green-800 dark:text-green-200' : ''}`}
                />
              </SelectTrigger>
              <SelectContent>
                {SENIORITY_LEVELS.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company-size" className="text-sm font-medium text-linkedin">
              Company size target *
            </Label>
            <Select 
              value={data.companySize || ""} 
              onValueChange={(value) => updateData({ companySize: value })}
            >
              <SelectTrigger className={`border-linkedin/20 ${data.companySize ? 'border-green-500 bg-green-50 dark:bg-green-950 ring-2 ring-green-200' : ''}`}>
                <SelectValue 
                  placeholder="Select company size" 
                  className={`${data.companySize ? 'text-green-800 dark:text-green-200' : ''}`}
                />
              </SelectTrigger>
              <SelectContent>
                {COMPANY_SIZES.map((size) => (
                  <SelectItem key={size} value={size}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {(data.audienceSeniority || data.companySize) && (
        <Card className="p-4 bg-linkedin-light dark:bg-linkedin-dark/20 border-linkedin/20">
          <h3 className="font-semibold text-linkedin mb-2">
            Target Audience Summary:
          </h3>
          <p className="text-linkedin/80 text-sm space-y-1">
            {data.audienceSeniority && (
              <>
                <strong>Seniority:</strong> {data.audienceSeniority} professionals<br />
              </>
            )}
            {data.companySize && (
              <>
                <strong>Company Size:</strong> {data.companySize}
              </>
            )}
          </p>
        </Card>
      )}
    </div>
  );
}